import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, CheckCircle2 } from "lucide-react";
import { useEffect } from "react";

export default function ServiceDetailModal({ service, onClose }) {
  useEffect(() => {
    if (!service) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [service, onClose]);

  return (
    <AnimatePresence>
      {service && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="service-detail-title"
          data-testid="service-detail-modal"
        >
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            className="relative bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto"
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <button
              data-testid="service-modal-close"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white dark:bg-slate-950/95 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 transition-colors"
            >
              <X size={18} className="text-slate-700 dark:text-slate-300" />
            </button>

            {/* Hero image */}
            <div className="relative h-56 md:h-72 overflow-hidden bg-slate-100 dark:bg-slate-800">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/30 to-transparent" aria-hidden="true" />
              <div className="absolute bottom-0 left-0 right-0 px-6 md:px-10 pb-6">
                <div className="text-xs uppercase tracking-[0.3em] text-[#0891B2] mb-2">
                  {service.tagline}
                </div>
                <h2
                  id="service-detail-title"
                  className="font-heading font-black text-3xl md:text-5xl text-slate-900 dark:text-white tracking-tight"
                >
                  {service.title}
                </h2>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 md:px-10 py-10 grid grid-cols-1 md:grid-cols-12 gap-10">
              <div className="md:col-span-7">
                <div className="text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-500 mb-3">Overview</div>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-base mb-8">{service.overview}</p>

                <div className="text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-500 mb-4">How we do it</div>
                <ol className="space-y-5">
                  {service.process.map((step, i) => (
                    <li key={i} className="flex gap-4" data-testid={`service-step-${i}`}>
                      <div className="flex-shrink-0 w-9 h-9 border border-[#0891B2] bg-[#0891B2]/5 flex items-center justify-center font-heading font-bold text-sm text-[#0891B2]">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-slate-900 dark:text-white text-base mb-1">{step.title}</h4>
                        <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{step.detail}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="md:col-span-5">
                <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 mb-6">
                  <div className="text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-500 mb-4">What you get</div>
                  <ul className="space-y-3">
                    {service.deliverables.map((d, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-800 dark:text-slate-100">
                        <CheckCircle2 size={16} className="text-[#0891B2] mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border border-slate-200 dark:border-slate-800 p-6">
                  <div className="text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-500 mb-2">Result signal</div>
                  <div className="font-heading font-black text-3xl text-slate-900 dark:text-white mb-4">{service.metric}</div>
                  <a
                    href="#contact"
                    onClick={onClose}
                    data-testid="service-modal-cta"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#007AFF] hover:bg-[#0056d3] text-white text-sm font-medium px-6 py-3 shadow-[0_4px_20px_rgba(0,122,255,0.25)] hover:shadow-[0_6px_28px_rgba(0,122,255,0.4)] transition-colors"
                  >
                    Book a consultation <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
